import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, Plus, Search, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import StatusPill from "@/components/StatusPill";

/**
 * Searchable meter picker — matches by household name, IMEI/DevEUI or meter number.
 */
function MeterSearchPicker({ value, onChange, meters }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = meters.find((m) => m.id === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return meters.slice(0, 50);
    return meters.filter((m) =>
      (m.serial_number || "").toLowerCase().includes(q) ||
      (m.imei || "").toLowerCase().includes(q) ||
      (m.deveui || "").toLowerCase().includes(q) ||
      (m.customer_name || "").toLowerCase().includes(q) ||
      (m._household_name || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [query, meters]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="payment-meter-trigger"
          className="w-full h-10 flex items-center justify-between gap-2 border border-slate-300 rounded-sm bg-white px-3 text-sm text-left hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
        >
          <span className={selected ? "text-slate-800" : "text-slate-400"}>
            {selected
              ? <>{selected.serial_number}<span className="text-slate-400 font-mono ml-2">· {(selected.imei || selected.deveui || "").slice(-6)}</span>{selected._household_name && <span className="ml-2 text-slate-500">· {selected._household_name}</span>}</>
              : "Search meter by household, IMEI or meter number"}
          </span>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] p-0 max-h-[400px] overflow-hidden flex flex-col">
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              data-testid="payment-meter-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by household, IMEI or meter number…"
              className="w-full pl-7 pr-2 py-1.5 text-sm rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-6">No matches</div>
          ) : filtered.map((m) => (
            <button
              type="button"
              key={m.id}
              onClick={() => { onChange(m.id); setOpen(false); setQuery(""); }}
              data-testid={`payment-meter-option-${m.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-100 text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">
                  {m._household_name || m.customer_name || m.serial_number}
                </div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {m.serial_number} {m.imei ? `· IMEI ${m.imei}` : m.deveui ? `· DevEUI ${m.deveui}` : ""}
                </div>
              </div>
              {m.id === value && <Check size={14} className="text-brand-600" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const ChannelPill = ({ value }) => {
  const map = {
    USER:   { label: "User",   cls: "bg-brand-50 text-brand-700 border-brand-200" },
    MPESA:  { label: "M-Pesa", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    MTN:    { label: "MTN",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
    AIRTEL: { label: "Airtel", cls: "bg-red-50 text-red-700 border-red-200" },
    API:    { label: "API",    cls: "bg-slate-100 text-slate-600 border-slate-200" },
    SYSTEM: { label: "System", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  };
  const m = map[value] || { label: value || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
};

export default function Payments() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meters, setMeters] = useState([]);
  const [config, setConfig] = useState(null);
  const [search, setSearch] = useState("");

  // Top-up dialog
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ meter_id: "", amount: "", note: "" });
  const [err, setErr] = useState("");

  const load = async (q = "") => {
    setLoading(true);
    try {
      const params = q ? { search: q, limit: 200 } : { limit: 200 };
      const { data } = await api.get("/recharge-orders", { params });
      setOrders(data.data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load payments");
    } finally { setLoading(false); }
  };

  const loadRefs = async () => {
    try {
      const [m, hh, cfg] = await Promise.all([
        api.get("/meters", { params: { page_size: 500 } }),
        api.get("/households", { params: { page_size: 500 } }),
        api.get("/config"),
      ]);
      const houseById = Object.fromEntries((hh.data.data || []).map(h => [h.id, h.household_name]));
      setMeters((m.data.data || []).map(x => ({ ...x, _household_name: x.household_id ? houseById[x.household_id] : null })));
      setConfig(cfg.data.data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load references");
    }
  };

  useEffect(() => { loadRefs(); load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const openTopUp = () => {
    setForm({ meter_id: "", amount: "", note: "" });
    setErr(""); setOpen(true);
  };

  const selectedMeter = meters.find((m) => m.id === form.meter_id);
  const price = Number(selectedMeter?.price_per_m3 || 0);
  const amt = Number(form.amount) || 0;
  const derivedM3 = price > 0 ? +(amt / price).toFixed(3) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.meter_id) { setErr("Please select a meter"); return; }
    if (!amt || amt <= 0) { setErr("Amount must be positive"); return; }
    setSaving(true);
    try {
      await api.post(`/meters/${form.meter_id}/recharge`, {
        amount: amt,
        reason: form.note || null,
      });
      toast.success(`Top-up of UGX ${amt.toLocaleString()} queued (${derivedM3} m³)`);
      setOpen(false);
      load(search);
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Top-up failed");
    } finally { setSaving(false); }
  };

  const fmtUgx = (v) => `UGX ${(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Billing</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Payments</h1>
          <p className="text-sm text-slate-500 mt-1">
            Top-ups, deductions and reconciliation against meter ACKs.
            {config && <> Price per m³ depends on each household&apos;s cylinder type (Configurations).</>}
          </p>
        </div>
        <Button onClick={openTopUp} data-testid="payments-topup-btn"
          className="bg-brand-600 hover:bg-brand-700 rounded-sm">
          <Plus size={16} className="mr-1" /> Top up meter
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by household, IMEI, meter number, channel…"
              className="pl-9 rounded-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="payments-search-input"
            />
          </div>
          <div className="ml-auto text-xs text-slate-500 tabular-nums">{orders.length} entries</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Order #</th>
                <th className="text-left px-4 py-3 font-semibold">Household · Meter</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-right px-4 py-3 font-semibold">Gas (m³)</th>
                <th className="text-left px-4 py-3 font-semibold">Channel</th>
                <th className="text-left px-4 py-3 font-semibold">By</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400 flex flex-col items-center gap-2">
                  <CreditCard size={22} className="text-slate-300" />
                  <span>No payments yet.</span>
                </td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payment-row-${o.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{o.order_no}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{o.household_name || <span className="text-slate-400 italic">Unassigned</span>}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {o.meter_serial || "—"}{o.meter_imei ? ` · ${o.meter_imei}` : ""}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold ${o.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {o.amount < 0 ? "−" : "+"}{fmtUgx(Math.abs(o.amount))}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                    {(o.gas_amount_m3 ?? 0).toFixed(3)}
                  </td>
                  <td className="px-4 py-3"><ChannelPill value={o.channel} /></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{o.operator || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3"><StatusPill value={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Top up meter</DialogTitle>
            <DialogDescription>
              Amount is converted to gas at the household&apos;s cylinder-type price
              {selectedMeter && <> (<span className="font-semibold" data-testid="topup-price-per-m3">UGX {price.toLocaleString()}/m³</span>)</>}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Meter</Label>
              <div className="mt-1">
                <MeterSearchPicker
                  value={form.meter_id}
                  onChange={(v) => setForm({ ...form, meter_id: v })}
                  meters={meters}
                />
              </div>
              {selectedMeter && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Current balance: <span className="font-mono tabular-nums">{fmtUgx(selectedMeter.balance)}</span>
                  {" · "}Remaining gas: <span className="font-mono tabular-nums">{(selectedMeter.remaining_flow_m3 ?? 0).toFixed(3)} m³</span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Amount (UGX)</Label>
              <Input
                type="number"
                inputMode="numeric"
                min="1"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="topup-amount-input"
                className="mt-1 rounded-sm tabular-nums"
                placeholder="e.g. 17000"
                autoFocus
              />
              <div className="mt-2 flex items-center justify-between text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <span>Gas credited</span>
                <span className="font-heading text-lg font-bold text-slate-900 tabular-nums" data-testid="topup-derived-m3">
                  {derivedM3.toFixed(3)} m³
                </span>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Note <span className="text-slate-400 normal-case">(optional)</span></Label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="topup-note-input"
                className="mt-1 rounded-sm"
                placeholder="Cash paid at depot"
              />
            </div>
            {err && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2 rounded-sm" data-testid="topup-error">
                {err}
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="topup-submit"
                className="bg-brand-600 hover:bg-brand-700 rounded-sm">
                {saving ? "Saving…" : "Save top-up"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
